import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getPublishedBlogs, type Blog } from '../services/blogService';
import Navbar from './Navbar';
import Footer from './Footer';
import { FaCalendar, FaUser, FaArrowRight, FaSpinner } from 'react-icons/fa';

export function Blogs() {
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Get brandId from environment or use a default
  const brandId = import.meta.env.VITE_BRAND_ID ;

  const fetchBlogs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getPublishedBlogs(brandId, currentPage, 9);
      setBlogs(response.data);
      setTotalPages(response.meta.totalPages);
      setError(null);
    } catch (err) {
      setError('Failed to load blogs. Please try again later.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [brandId, currentPage]);

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Recently';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="min-w-screen bg-[#141237ff]">
      <Navbar />

      {/* Hero Section */}
      <div className="w-full bg-gradient-to-br from-pink-600 via-red-500 to-pink-700 py-20 px-4 mt-20">
        <div className="max-w-[1024px] mx-auto text-center">
          <h1 className="font-['Inter'] text-[48px] font-[700] leading-[56px] text-white mb-4 mobile:text-[32px] mobile:leading-[40px]">
            Our Blog
          </h1>
          <p className="font-['Afacad_Flux'] text-[20px] font-[400] leading-[32px] text-white/90 mobile:text-[18px] mobile:leading-[28px]">
            Stay updated with the latest news, tips, and insights
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full px-4 py-16 mobile:py-12">
        <div className="max-w-[1024px] mx-auto">
          {loading && (
            <div className="flex justify-center items-center py-20">
              <FaSpinner className="animate-spin text-pink-500 text-4xl" />
            </div>
          )}

          {error && !loading && (
            <div className="bg-red-500/10 border border-red-500 rounded-lg p-6 text-center">
              <p className="text-red-400 text-lg">{error}</p>
              <button
                onClick={fetchBlogs}
                className="mt-4 px-6 py-2 bg-pink-600 text-white rounded-full hover:bg-pink-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          )}

          {!loading && !error && blogs.length === 0 && (
            <div className="text-center py-20">
              <p className="text-white/60 text-xl">No blog posts available yet.</p>
              <p className="text-white/40 text-sm mt-2">Check back soon for updates!</p>
            </div>
          )}

          {!loading && !error && blogs.length > 0 && (
            <>
              {/* Blog Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mobile:gap-4">
                {blogs.map((blog) => (
                  <Link
                    key={blog.id}
                    to={`/blogs/${blog.slug}`}
                    className="group"
                  >
                    <div className="bg-white/5 backdrop-blur-sm rounded-xl overflow-hidden border border-white/10 hover:border-pink-500/50 transition-all duration-300 hover:transform hover:scale-[1.02] h-full flex flex-col">
                      {/* Featured Image */}
                      {blog.featuredImage ? (
                        <div className="w-full h-48 overflow-hidden">
                          <img
                            src={blog.featuredImage}
                            alt={blog.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                        </div>
                      ) : (
                        <div className="w-full h-48 bg-gradient-to-br from-pink-600/20 to-red-500/20 flex items-center justify-center">
                          <span className="text-white/40 text-6xl">📝</span>
                        </div>
                      )}

                      {/* Content */}
                      <div className="p-6 flex flex-col flex-grow">
                        <h3 className="text-white font-['Inter'] text-[20px] font-[600] leading-[28px] mb-3 group-hover:text-pink-400 transition-colors line-clamp-2">
                          {blog.title}
                        </h3>

                        {blog.excerpt && (
                          <p className="text-white/60 font-['Afacad_Flux'] text-[16px] leading-[24px] mb-4 line-clamp-3 flex-grow">
                            {blog.excerpt}
                          </p>
                        )}

                        {/* Meta Info */}
                        <div className="flex flex-wrap items-center gap-4 text-white/50 text-sm mt-auto">
                          {blog.author && (
                            <div className="flex items-center gap-2">
                              <FaUser className="text-xs" />
                              <span>{blog.author}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <FaCalendar className="text-xs" />
                            <span>{formatDate(blog.publishedDate)}</span>
                          </div>
                        </div>

                        {/* Read More */}
                        <div className="flex items-center gap-2 text-pink-400 mt-4 group-hover:gap-3 transition-all">
                          <span className="font-medium">Read More</span>
                          <FaArrowRight className="text-sm" />
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-12">
                  <button
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 bg-white/5 text-white rounded-lg hover:bg-white/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>

                  <div className="flex gap-2">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-4 py-2 rounded-lg transition-colors ${
                          currentPage === page
                            ? 'bg-pink-600 text-white'
                            : 'bg-white/5 text-white hover:bg-white/10'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 bg-white/5 text-white rounded-lg hover:bg-white/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default Blogs;
